import React, { useMemo, useState } from "react"
import { Information } from "../../simulation/types"
import { resolveTreeNodeId } from "../utils/tree-resolver"

type Props = {
  categories: Information[]
  initialPath?: string[]
  onSelect: (path: string[]) => void
  onCancel?: () => void
}

const TreeNode: React.FC<{
  node: Information
  path: string[]
  depth: number
  selectedPath: string[]
  onPick: (path: string[]) => void
}> = ({ node, path, depth, selectedPath, onPick }) => {
  const [expanded, setExpanded] = useState(depth < 2)
  const children = node.categories || []
  const isLeaf = children.length === 0
  const fullPath = [...path, node.text]
  const isSelected =
    selectedPath.length === fullPath.length &&
    selectedPath.every((seg, i) => seg === fullPath[i])

  return (
    <div className="ml-2">
      <div className="flex items-center gap-1 py-0.5">
        {!isLeaf && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="w-4 h-4 text-xs text-gray-500 hover:text-gray-800"
            aria-label={expanded ? "Collapse" : "Expand"}>
            {expanded ? "▾" : "▸"}
          </button>
        )}
        {isLeaf && <span className="w-4 h-4 inline-block" />}
        <button
          type="button"
          onClick={() => onPick(fullPath)}
          className={`text-left text-sm px-1 rounded hover:bg-indigo-50 ${
            isSelected ? "bg-indigo-100 font-semibold" : ""
          }`}>
          {node.text}
        </button>
      </div>
      {expanded && children.length > 0 && (
        <div className="border-l border-gray-200">
          {children.map((c) => (
            <TreeNode
              key={`${depth}-${c.id ?? c.text}`}
              node={c}
              path={fullPath}
              depth={depth + 1}
              selectedPath={selectedPath}
              onPick={onPick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const TreeNodePicker: React.FC<Props> = ({
  categories,
  initialPath = [],
  onSelect,
  onCancel,
}) => {
  const [selectedPath, setSelectedPath] = useState<string[]>(initialPath)

  const ambiguity = useMemo(() => {
    if (selectedPath.length === 0) return { count: 0, resolved: null as number | null }
    const resolved = resolveTreeNodeId(categories, selectedPath)
    // Count ambiguity: how many nodes match the last segment substring
    const allMatches: number[] = []
    const visit = (nodes: Information[], path: string[]) => {
      for (const n of nodes) {
        const newPath = [...path, n.text]
        // crude: if last selected segment partial-matches, count it
        if (
          selectedPath.length > 0 &&
          n.text.toLowerCase().includes(selectedPath[selectedPath.length - 1].toLowerCase())
        ) {
          allMatches.push(n.id)
        }
        if (n.categories) visit(n.categories, newPath)
      }
    }
    visit(categories, [])
    return { count: allMatches.length, resolved }
  }, [categories, selectedPath])

  return (
    <div className="border border-gray-200 rounded p-2 bg-white">
      <div className="text-xs text-gray-500 mb-1">Pick a tree node</div>
      <div className="max-h-64 overflow-y-auto">
        {categories.map((c) => (
          <TreeNode
            key={c.id ?? c.text}
            node={c}
            path={[]}
            depth={0}
            selectedPath={selectedPath}
            onPick={setSelectedPath}
          />
        ))}
      </div>
      <div className="text-xs text-gray-600 border-t pt-2 mt-2">
        <div>
          <span className="font-medium">Path:</span>{" "}
          {selectedPath.length > 0 ? selectedPath.join(" → ") : "(none)"}
        </div>
        <div>
          <span className="font-medium">Resolves to:</span>{" "}
          {ambiguity.resolved !== null
            ? `#tree-item-${ambiguity.resolved}`
            : "(unresolved)"}
        </div>
        {ambiguity.count > 1 && (
          <div className="text-amber-600">
            ⚠ {ambiguity.count} nodes share the last segment — consider a more specific path.
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-3 py-1 border rounded">
            Cancel
          </button>
        )}
        <button
          type="button"
          disabled={selectedPath.length === 0}
          onClick={() => onSelect(selectedPath)}
          className="text-sm px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50">
          Use this path
        </button>
      </div>
    </div>
  )
}

export default TreeNodePicker
